import { AppDispatch } from '../index';
import {
  setRules,
  addRule,
  updateRule,
  removeRule,
  setEvents,
  setRunStatus,
  setWhatsAppStatus,
  setAlertStates,
  setWatchEntries,
  addWatch,
  removeWatch,
  setLoading,
  setError,
} from '../slices/alertsSlice';
import {
  getMATouchAlertRules,
  createMATouchAlertRule,
  updateMATouchAlertRule,
  deleteMATouchAlertRule,
  enableMATouchAlertRule,
  disableMATouchAlertRule,
  getMATouchAlertEvents,
  getMATouchAlertStatus,
  sendMATouchTestEmail,
  getTelegramAlertStatus,
  createTelegramConnectLink,
  sendTelegramTestMessage,
  deleteTelegramConnection,
  getWhatsAppAlertStatus,
  createWhatsAppConnection,
  verifyWhatsAppConnection,
  sendWhatsAppTestMessage,
  deleteWhatsAppConnection,
  getAlertStateMachineStatus,
  getAlertFilterConfig,
  updateAlertFilterConfig,
  cloneMainFiltersForAlert,
  getBuyActionWatchlist,
  createBuyActionWatch as createBuyActionWatchApi,
  deleteBuyActionWatch as deleteBuyActionWatchApi,
} from '../../services/api';
import {
  MATouchAlertEvent,
  MATouchAlertRule,
  MATouchAlertRulePayload,
  BuyActionWatch,
  BuyActionWatchPayload,
} from '../../types';

const sanitizeAlertRule = (value: unknown): MATouchAlertRule | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const maybeRule = value as Partial<MATouchAlertRule>;
  const id = maybeRule.id != null ? String(maybeRule.id) : '';
  if (!id) {
    return null;
  }

  return {
    ...(maybeRule as MATouchAlertRule),
    id,
    enabled: Boolean(maybeRule.enabled),
    email_enabled: maybeRule.email_enabled !== undefined ? Boolean(maybeRule.email_enabled) : true,
    telegram_enabled: maybeRule.telegram_enabled !== undefined ? Boolean(maybeRule.telegram_enabled) : false,
    whatsapp_enabled: maybeRule.whatsapp_enabled !== undefined ? Boolean(maybeRule.whatsapp_enabled) : false,
    cooldown_hours: maybeRule.cooldown_hours !== undefined ? Number(maybeRule.cooldown_hours) : 24,
    cooldown_reset_distance_pct: maybeRule.cooldown_reset_distance_pct !== undefined ? Number(maybeRule.cooldown_reset_distance_pct) : 2.0,
  };
};

const sanitizeAlertEvent = (value: unknown): MATouchAlertEvent | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const maybeEvent = value as Partial<MATouchAlertEvent>;
  if (maybeEvent.id === undefined || maybeEvent.id === null) {
    return null;
  }

  return maybeEvent as MATouchAlertEvent;
};

export const sanitizeAlertRules = (rules: unknown): MATouchAlertRule[] => {
  if (!Array.isArray(rules)) {
    return [];
  }

  return rules
    .map((rule) => sanitizeAlertRule(rule))
    .filter((rule): rule is MATouchAlertRule => Boolean(rule));
};

export const sanitizeAlertEvents = (events: unknown): MATouchAlertEvent[] => {
  if (!Array.isArray(events)) {
    return [];
  }

  return events
    .map((event) => sanitizeAlertEvent(event))
    .filter((event): event is MATouchAlertEvent => Boolean(event));
};

// Buy-Action watch sanitizers - coerce booleans + require a ticker, mirroring
// the MA-touch rule sanitizer's defensive shape.
const sanitizeBuyActionWatch = (value: unknown): BuyActionWatch | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const maybe = value as Partial<BuyActionWatch>;
  const ticker = maybe.ticker != null ? String(maybe.ticker).trim().toUpperCase() : '';
  if (!ticker) {
    return null;
  }

  return {
    ticker,
    baseline_entry_status:
      maybe.baseline_entry_status != null ? String(maybe.baseline_entry_status) : 'unknown',
    email_enabled: Boolean(maybe.email_enabled),
    telegram_enabled: Boolean(maybe.telegram_enabled),
    whatsapp_enabled: Boolean(maybe.whatsapp_enabled),
    created_at: maybe.created_at != null ? String(maybe.created_at) : '',
  };
};

export const sanitizeBuyActionWatches = (watches: unknown): BuyActionWatch[] => {
  if (!Array.isArray(watches)) {
    return [];
  }

  return watches
    .map((watch) => sanitizeBuyActionWatch(watch))
    .filter((watch): watch is BuyActionWatch => Boolean(watch));
};

// Thunks
export const fetchAlertRules = (includeDisabled: boolean = false) => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const response = await getMATouchAlertRules(includeDisabled);
    dispatch(setRules(sanitizeAlertRules(response.rules)));
  } catch (error: any) {
    const message = error.response?.data?.error || 'Failed to fetch alert rules';
    dispatch(setError(message));
  } finally {
    dispatch(setLoading(false));
  }
};

export const createAlertRule = (payload: MATouchAlertRulePayload) => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const response = await createMATouchAlertRule(payload);
    const safeRule = sanitizeAlertRule(response.rule);
    if (safeRule) {
      dispatch(addRule(safeRule));
    }
    return safeRule;
  } catch (error: any) {
    const message = error.response?.data?.error || 'Failed to create alert rule';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const editAlertRule = (ruleId: string, payload: Partial<MATouchAlertRulePayload>) => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const response = await updateMATouchAlertRule(ruleId, payload);
    const safeRule = sanitizeAlertRule(response.rule);
    if (!safeRule) {
      throw new Error('Invalid alert rule response');
    }
    dispatch(updateRule(safeRule));
    return safeRule;
  } catch (error: any) {
    const message = error.response?.data?.error || 'Failed to update alert rule';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const deleteAlertRule = (ruleId: string) => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    await deleteMATouchAlertRule(ruleId);
    dispatch(removeRule(ruleId));
  } catch (error: any) {
    const message = error.response?.data?.error || 'Failed to delete alert rule';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const toggleAlertRule = (ruleId: string, enabled: boolean) => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const response = enabled
      ? await enableMATouchAlertRule(ruleId)
      : await disableMATouchAlertRule(ruleId);
    const safeRule = sanitizeAlertRule(response.rule);
    if (!safeRule) {
      throw new Error('Invalid alert rule response');
    }
    dispatch(updateRule(safeRule));
    return safeRule;
  } catch (error: any) {
    const message = error.response?.data?.error || `Failed to ${enabled ? 'enable' : 'disable'} alert rule`;
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const fetchAlertEvents = (limit: number = 50, offset: number = 0) => async (dispatch: AppDispatch) => {
  // Background poll: do NOT touch shared loading / error so we don't wipe out
  // user-facing errors set by user-initiated thunks (create/edit/delete).
  // The Recent Events table renders silently when this fails - acceptable.
  try {
    const response = await getMATouchAlertEvents(limit, offset);
    dispatch(setEvents(sanitizeAlertEvents(response.events)));
  } catch (error: any) {
    // Log only; do not stomp the shared error banner.
    // eslint-disable-next-line no-console
    console.warn('fetchAlertEvents failed:', error?.response?.data?.error || error?.message);
  }
};

export const fetchAlertStatus = () => async (dispatch: AppDispatch) => {
  // Background poll: see fetchAlertEvents above.
  try {
    const status = await getMATouchAlertStatus();
    dispatch(setRunStatus(status));
  } catch (error: any) {
    // eslint-disable-next-line no-console
    console.warn('fetchAlertStatus failed:', error?.response?.data?.error || error?.message);
  }
};

export const sendTestEmail = (email: string) => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const response = await sendMATouchTestEmail(email);
    return response;
  } catch (error: any) {
    const data = error.response?.data;
    let message = data?.error || 'Failed to send test email';
    if (data?.account_email) {
      message = `${message} (signed in as ${data.account_email})`;
    }
    // Component renders this via local testEmailError state. Avoid populating the
    // shared alerts.error slice so we don't double-render the same message in the
    // top-of-page banner.
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const fetchTelegramStatus = () => async (_dispatch: AppDispatch) => {
  // Background-safe: do NOT touch shared loading / error so this can be
  // dispatched on AlertsTab mount (for QuickStart channel-state derivation)
  // without flickering the spinner or wiping unrelated user-action errors.
  // Callers that need to surface fetch failures to the user should handle
  // the rejection locally.
  try {
    return await getTelegramAlertStatus();
  } catch (error: any) {
    const message = error.response?.data?.error || 'Failed to fetch Telegram status';
    // Log only - components handle locally if they care.
    // eslint-disable-next-line no-console
    console.warn('fetchTelegramStatus failed:', message);
    throw new Error(message);
  }
};

export const createTelegramLink = () => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    return await createTelegramConnectLink();
  } catch (error: any) {
    const message = error.response?.data?.error || 'Failed to create Telegram link';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const sendTestTelegram = () => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    return await sendTelegramTestMessage();
  } catch (error: any) {
    const message = error.response?.data?.error || 'Failed to send Telegram test message';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const disconnectTelegram = () => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    return await deleteTelegramConnection();
  } catch (error: any) {
    const message = error.response?.data?.error || 'Failed to disconnect Telegram';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

// WhatsApp thunks
export const fetchWhatsAppStatus = () => async (dispatch: AppDispatch) => {
  // Background-safe: do NOT touch shared `error` slice. This thunk is
  // dispatched on AlertsTab mount (for QuickStart `hasUsableChannel`),
  // and a flaky /whatsapp/status response on load should not pollute the
  // top-of-page error banner with an unrelated message. Callers that
  // need to surface failures locally can catch the rejected promise.
  try {
    const status = await getWhatsAppAlertStatus();
    dispatch(setWhatsAppStatus(status));
    return status;
  } catch (error: any) {
    const message = error.response?.data?.error || 'Failed to fetch WhatsApp status';
    // eslint-disable-next-line no-console
    console.warn('fetchWhatsAppStatus failed:', message);
    throw new Error(message);
  }
};

export const connectWhatsApp = (phoneNumber: string) => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    return await createWhatsAppConnection(phoneNumber);
  } catch (error: any) {
    const message = error.response?.data?.error || 'Failed to connect WhatsApp';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const verifyWhatsApp = (code: string) => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const result = await verifyWhatsAppConnection(code);
    // Refresh WhatsApp status - must await so the UI reflects verified state
    // before the caller proceeds. Swallow refresh-only errors; the verify
    // result is what matters.
    try {
      await dispatch(fetchWhatsAppStatus());
    } catch {
      // Refresh failure is non-fatal; verify already succeeded.
    }
    return result;
  } catch (error: any) {
    const message = error.response?.data?.error || 'Failed to verify WhatsApp';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const sendTestWhatsApp = () => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    return await sendWhatsAppTestMessage();
  } catch (error: any) {
    const message = error.response?.data?.error || 'Failed to send WhatsApp test message';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const disconnectWhatsApp = () => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    await deleteWhatsAppConnection();
    dispatch(setWhatsAppStatus(null));
  } catch (error: any) {
    const message = error.response?.data?.error || 'Failed to disconnect WhatsApp';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

// Alert State Machine thunks
export const fetchAlertStates = (stateFilter?: string) => async (dispatch: AppDispatch) => {
  try {
    const response = await getAlertStateMachineStatus(stateFilter);
    dispatch(setAlertStates(response.states || []));
    return response;
  } catch (error: any) {
    const message = error.response?.data?.error || 'Failed to fetch alert states';
    dispatch(setError(message));
  }
};

// Alert Filter Config thunks
export const fetchAlertFilterConfig = (ruleId: string) => async (_dispatch: AppDispatch) => {
  try {
    return await getAlertFilterConfig(ruleId);
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to fetch alert filter config');
  }
};

export const saveAlertFilterConfig = (ruleId: string, config: Record<string, any>) => async (_dispatch: AppDispatch) => {
  try {
    return await updateAlertFilterConfig(ruleId, config);
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to save alert filter config');
  }
};

export const cloneMainFilters = (ruleId: string) => async (_dispatch: AppDispatch) => {
  try {
    return await cloneMainFiltersForAlert(ruleId);
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to clone main filters');
  }
};

// Buy-Action watchlist thunks (one-shot buy-zone watches) -
// PLAN_BUY_ACTION_ALERT_2026-06-20. Clones the MA-touch CRUD pattern.
export const fetchBuyActionWatchlist = () => async (dispatch: AppDispatch) => {
  // Review F2: background-safe - do NOT touch the shared alerts.loading/error
  // slice (matches fetchAlertEvents/fetchAlertStatus). A chart-button mount
  // refetch must not blank a create/delete error banner shown elsewhere.
  try {
    const response = await getBuyActionWatchlist();
    dispatch(setWatchEntries(sanitizeBuyActionWatches(response.watches)));
  } catch {
    // Silent: a background watchlist refresh failing should not surface a
    // global error; the list simply keeps its prior contents.
  }
};

export const createBuyActionWatch = (payload: BuyActionWatchPayload) => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  // Normalize the ticker + force booleans before hitting the API so callers
  // (chart picker, watchlist add-input) can pass loose shapes.
  const safePayload: BuyActionWatchPayload = {
    ticker: String(payload.ticker || '').trim().toUpperCase(),
    email_enabled: Boolean(payload.email_enabled),
    telegram_enabled: Boolean(payload.telegram_enabled),
    whatsapp_enabled: Boolean(payload.whatsapp_enabled),
  };

  try {
    const response = await createBuyActionWatchApi(safePayload);
    const safeWatch = sanitizeBuyActionWatch(response.watch);
    if (safeWatch) {
      dispatch(addWatch(safeWatch));
    }
    return safeWatch;
  } catch (error: any) {
    const message = error.response?.data?.error || 'Failed to create buy-action watch';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};

export const deleteBuyActionWatch = (ticker: string) => async (dispatch: AppDispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  const safeTicker = String(ticker || '').trim().toUpperCase();

  try {
    await deleteBuyActionWatchApi(safeTicker);
    dispatch(removeWatch(safeTicker));
  } catch (error: any) {
    // Review F3: if the row is already gone server-side (one-shot auto-delete
    // raced the user's manual remove), still drop it locally so the UI
    // self-heals instead of stranding a stale "Watching" pill.
    if (error.response?.status === 404 || error.response?.status === 410) {
      dispatch(removeWatch(safeTicker));
      return;
    }
    const message = error.response?.data?.error || 'Failed to delete buy-action watch';
    dispatch(setError(message));
    throw new Error(message);
  } finally {
    dispatch(setLoading(false));
  }
};
